import React, { useMemo, useState } from "react";
import { useFrequentlyAsked, useRecentDocuments, useSuggestions, useUnansweredQuestions } from "../../hooks/useOverviewLists";

export default function RecentDocuments() {
  const [active, setActive] = useState("recent");
  const { data: recent, loading: loadingRecent } = useRecentDocuments(5, 30);
  const { data: faq, loading: loadingFaq } = useFrequentlyAsked(10, 30);
  const { data: sugg, loading: loadingSugg } = useSuggestions(10);
  const { data: unanswered, loading: loadingUnans } = useUnansweredQuestions(10, 30);

  const { rows, loading } = useMemo(() => {
    switch (active) {
      case "faq":
        return { rows: faq, loading: loadingFaq };
      case "sugg":
        return { rows: sugg, loading: loadingSugg };
      case "unans":
        return { rows: unanswered, loading: loadingUnans };
      case "recent":
      default:
        return { rows: recent, loading: loadingRecent };
    }
  }, [active, recent, loadingRecent, faq, loadingFaq, sugg, loadingSugg, unanswered, loadingUnans]);

  const formatDate = (val) => {
    if (!val) return "—";
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return "—";
      return new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit"
      }).format(d);
    } catch {
      return "—";
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 pt-4">
        <h3 className="text-[18px] font-semibold text-slate-800">Recent Documents Searched</h3>
      </div>
      {/* Tabs */}
      <div className="flex border-b text-sm font-medium text-gray-500 mt-2 overflow-x-auto no-scrollbar">
        <button
          className={`px-5 py-3 whitespace-nowrap ${active === "recent" ? "text-green-500 border-b-2 border-green-500" : "hover:text-gray-700"}`}
          onClick={() => setActive("recent")}
        >
          Recent Documents
        </button>
        <button
          className={`px-5 py-3 whitespace-nowrap ${active === "faq" ? "text-green-500 border-b-2 border-green-500" : "hover:text-gray-700"}`}
          onClick={() => setActive("faq")}
        >
          Frequently Asked
        </button>
        <button
          className={`px-5 py-3 whitespace-nowrap ${active === "sugg" ? "text-green-500 border-b-2 border-green-500" : "hover:text-gray-700"}`}
          onClick={() => setActive("sugg")}
        >
          Suggestions
        </button>
        <button
          className={`px-5 py-3 whitespace-nowrap ${active === "unans" ? "text-green-500 border-b-2 border-green-500" : "hover:text-gray-700"}`}
          onClick={() => setActive("unans")}
        >
          Unanswered Questions
        </button>
      </div>

      {/* Rows */}
      <div className="divide-y">
        {loading && (
          <div className="px-5 py-6 text-xs text-gray-500">Loading...</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="px-5 py-6 text-xs text-gray-500">No data available.</div>
        )}
        {!loading && rows.map((doc, idx) => (
          <div
            key={idx}
            className="w-full flex flex-col md:flex-row md:items-center px-5 py-4 text-sm text-gray-900 gap-3 md:gap-0 overflow-hidden"
          >
            {/* 1️⃣ Document Info */}
            <div className="flex items-center gap-3 md:w-[30%] min-w-0">
              <div className="w-6 h-6 flex-shrink-0 bg-gray-100 rounded flex items-center justify-center">
                <span className="text-gray-400 text-sm">📄</span>
              </div>
              <div className="min-w-0">
                <p className="font-semibold truncate">{doc.title}</p>
                <p className="text-xs text-gray-500 truncate">on {doc.platform}</p>
              </div>
            </div>

            {/* 2️⃣ Searching Date */}
            <div className="md:w-[20%] min-w-0">
              <p className="text-xs font-medium text-gray-500">Searching Date</p>
              <p className="text-gray-900">{formatDate(doc.date)}</p>
            </div>

            {/* 3️⃣ Searching Count */}
            <div className="md:w-[25%] min-w-0">
              <p className="text-xs font-medium text-gray-500">Searching Count</p>
              <p className="text-gray-900">x{doc.count} Times this week</p>
            </div>

            {/* 4️⃣ User */}
            <div className="md:w-[25%] truncate min-w-0">
              <p className="text-xs font-medium text-gray-500">User</p>
              <p className="text-gray-900 truncate">{doc.user || "—"}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
